// pages/training/TrainingVideoPlayer.tsx
import React, { useCallback, useEffect, useRef } from 'react';
import { AspectRatio, Box, LinearProgress, Typography } from '@mui/joy';

/** How often playback position is pushed to the server while the video runs. */
const REPORT_INTERVAL_MS = 15000;

interface TrainingVideoPlayerProps {
  src: string;
  captionsUrl?: string;
  title?: string;
  /** Where the learner got to last time, as a percentage of the video. */
  initialPercentage: number;
  watchedPercentage: number;
  onWatchedChange: (percentage: number) => void;
  onReport: (percentage: number, positionSeconds: number) => void;
}

/**
 * Video with a memory.
 *
 * Playback position is written back as `watch_percentage` (plus the exact
 * second in `progress_data`) so a video watched half way through in a truck
 * picks up where it left off — the learner API returns `watch_percentage`,
 * which is what we resume from.
 */
export const TrainingVideoPlayer: React.FC<TrainingVideoPlayerProps> = ({
  src,
  captionsUrl,
  title,
  initialPercentage,
  watchedPercentage,
  onWatchedChange,
  onReport,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastReportAt = useRef(0);
  const lastReported = useRef(-1);
  // Read by the unmount flush, which must not re-run on every tick.
  const latest = useRef({ percentage: 0, position: 0 });
  const onReportRef = useRef(onReport);
  useEffect(() => {
    onReportRef.current = onReport;
  }, [onReport]);

  const report = useCallback((percentage: number, positionSeconds: number) => {
    if (percentage === lastReported.current) return;
    lastReported.current = percentage;
    lastReportAt.current = Date.now();
    onReportRef.current(percentage, positionSeconds);
  }, []);

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    // Resuming at 99% would just replay the credits; start over instead.
    if (initialPercentage > 0 && initialPercentage < 95) {
      video.currentTime = (initialPercentage / 100) * video.duration;
    }
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const percentage = Math.min(100, Math.floor((video.currentTime / video.duration) * 100));
    latest.current = { percentage, position: video.currentTime };
    onWatchedChange(percentage);
    if (Date.now() - lastReportAt.current >= REPORT_INTERVAL_MS) {
      report(percentage, video.currentTime);
    }
  };

  const flush = () => {
    const { percentage, position } = latest.current;
    if (percentage > 0) report(percentage, position);
  };

  // Leaving the page mid-video must not lose the position.
  useEffect(() => {
    return () => {
      const { percentage, position } = latest.current;
      if (percentage > 0 && percentage !== lastReported.current) {
        onReportRef.current(percentage, position);
      }
    };
  }, []);

  return (
    <Box>
      <AspectRatio ratio="16/9" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
        <video
          ref={videoRef}
          src={src}
          aria-label={title || 'Training video'}
          crossOrigin={captionsUrl ? 'anonymous' : undefined}
          controls
          playsInline
          preload="metadata"
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onPause={flush}
          onEnded={flush}
          style={{ width: '100%', height: '100%' }}
        >
          {captionsUrl && <track kind="captions" src={captionsUrl} srcLang="en" label="English captions" default />}
          Your browser does not support video playback.
        </video>
      </AspectRatio>
      <Box sx={{ mt: 1.5 }}>
        <Typography level="body-xs" sx={{ mb: 0.5 }}>
          {watchedPercentage}% watched
        </Typography>
        <LinearProgress determinate value={watchedPercentage} aria-label="Watched" />
      </Box>
    </Box>
  );
};

export default TrainingVideoPlayer;
